import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import DOMPurify from 'dompurify';
import { useEffect } from 'react';
import { Navigate, generatePath } from 'react-router';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare';
import { ORIGIN } from '@sorare/core/src/config';
import {
  BLOG,
  BLOG_SPORT,
  BLOG_SPORT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { loadScript } from '@sorare/core/src/lib/script';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BackButton } from '#src/components/BackButton/index.tsx';
import { ClassicBlock } from '#src/components/ClassicBlock/index.tsx';
import { Meta } from '#src/components/Meta/index.tsx';
import { PostSeo } from '#src/components/PostSeo/index.tsx';
import type { BlogSport } from '#src/constants/types.ts';
import { usePostImage } from '#src/hooks/usePostImage.ts';
import { getPostCategory } from '#src/lib/postCategory.ts';

import { ScrollToHash } from './ScrollToHash/index.tsx';
import type {
  getPost,
  getPostVariables,
} from './__generated__/index.graphql.ts';

const GET_POST = gql`
  query getPost($slug: ID!) {
    post(id: $slug, idType: SLUG) {
      ...getPostCategory_Post
      databaseId
      title
      date
      slug
      content
      rawExcerpt: excerpt(format: RENDERED)
      tags {
        nodes {
          id
          slug
        }
      }
      categories {
        nodes {
          name
        }
      }
      featuredImage {
        node {
          altText
          thumbnail: sourceUrl(size: MEDIUM)
          sourceUrl: sourceUrl(size: LARGE)
        }
      }
      alternateLanguagePosts {
        alternates {
          ... on Post {
            slug
            tags {
              edges {
                node {
                  name
                }
              }
            }
          }
        }
      }
      ...PostExcerpt_Meta
    }
  }
  ${Meta.fragment}
  ${getPostCategory.fragments.post}
` as TypedDocumentNode<getPost, getPostVariables>;

const Content = styled.div`
  font: var(--t-20);
  line-height: 140%;

  & .has-small-font-size {
    font: var(--t-16);
  }
  & .has-medium-font-size {
    font: var(--t-20);
  }
  & .has-large-font-size {
    font: var(--t-24);
  }
  & .has-x-large-font-size {
    font: var(--t-32);
  }
  & .has-xx-large-font-size {
    font: var(--t-48);
  }

  & a {
    text-decoration: underline;
  }

  & figure {
    margin: 1em 0 1em 0;
    img,
    div,
    iframe {
      width: 100%;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    margin-top: revert;
    line-height: 1.2em;
  }

  & ul {
    padding-left: 0;

    li {
      list-style-type: disc;
      margin-left: 30px;
    }
  }
`;

const Title = styled(HeadlineL)`
  display: inline-flex;
  min-width: 70%;
  flex-direction: column;
  background: var(--c-black);
  margin-top: -2em;
  padding: var(--double-unit) var(--triple-unit);

  @media (${tabletAndAbove}) {
    width: min-content;
  }
`;

const MetaAndSharing = styled(SBHorizontal)`
  padding-left: var(--triple-unit);
  margin-bottom: var(--quadruple-unit);
`;

const Share = styled(Horizontal)`
  justify-content: center;
`;

const NavigationContainer = styled(Horizontal)`
  justify-content: space-between;
`;

type Props = {
  preview?: boolean;
  sportCategory?: BlogSport | '';
  slug: string;
  language: string;
  category?: string;
};

declare global {
  interface Window {
    twttr: any;
  }
}

export const Post = ({
  preview,
  sportCategory = '',
  slug,
  language,
}: Props) => {
  const wp_token = useQueryString('wp_token');
  const { loading, data, error } = useQuery(GET_POST, {
    variables: { slug: slug! },
    context: preview
      ? {
          headers: {
            'X-WP-Nonce': wp_token,
          },
          credentials: 'include',
        }
      : undefined,
    skip: !slug,
  });
  const track = useEvents();
  const { setPageMetadata } = useSeoContext();
  const { showNotification } = useSnackNotificationContext();
  const { handleRef: bottomRef } = useIsVisibleInViewport<HTMLDivElement>({
    callback: () => {
      if (data?.post?.slug) {
        track('Read Blog Post', { slug: data.post.slug });
      }
    },
  });

  useEffect(() => {
    loadScript('https://platform.twitter.com/widgets.js').then(() => {
      setTimeout(() => window.twttr?.widgets.load(), 1000);
    });
  }, []);

  useEffect(() => {
    if (data?.post?.slug) {
      track('View Blog Post', { slug: data.post.slug });
    }
  }, [data?.post?.slug, track]);

  const postImage = usePostImage(data?.post, true);
  const description = DOMPurify.sanitize(data?.post?.rawExcerpt || '', {
    ALLOWED_TAGS: [],
  });

  useEffect(() => {
    if (data?.post?.title) {
      setPageMetadata(`${data.post.title} - Sorare Blog`, {
        description,
        img: postImage?.url,
      });
    }
  }, [data?.post, description, postImage?.url, setPageMetadata]);

  useEffect(() => {
    if (error) {
      showNotification('errors', { errors: [error.message] });
    }
  }, [error, showNotification]);

  useEffect(() => {
    const content = data?.post?.content;
    if (!content) return;

    for (const match of content.matchAll(/<a id="pd_a_([0-9]+)"><\/a>/g)) {
      const [, id] = match;
      loadScript(`https://secure.polldaddy.com/p/${id}.js`).then(() => {
        const poll = document.getElementById(`PDI_container${id}`);
        if (poll) {
          // cleanup the poll container to keep only the actual poll
          poll.parentElement!.replaceChildren(poll);

          // if the user has already voted, go to the results view
          if (document.cookie.includes(`PD_poll_${id}`)) {
            (window as any)[`PD_vote${id}`]?.(1);
          }
        }
      });
    }
  }, [data]);

  const errorElement = sportCategory ? (
    <Navigate
      to={generatePath(BLOG_SPORT, {
        sport: sportCategory,
      })}
    />
  ) : (
    <Navigate to={BLOG} />
  );

  if (error) {
    return errorElement;
  }

  if (loading || !data) return <LoadingIndicator fullHeight />;

  const { post } = data;

  if (!post) {
    return errorElement;
  }

  const sport = getPostCategory(post);

  if (sport && sportCategory !== sport) {
    return (
      <Navigate
        to={generatePath(BLOG_SPORT_SLUG, {
          sport,
          slug,
        })}
        replace
      />
    );
  }

  const { title, content } = post;

  const sharingProps = {
    title: title!,
    description,
    message: title || undefined,
    // oxlint-disable-next-line sorare/no-window-location
    url: window.location.href,
    image: {
      post: postImage?.url || null,
      story: postImage?.url || null,
      square: postImage?.url || null,
    },
    trackingEventName: socialShareEventName.SHARE_BLOG_POST,
    trackingEventContext: socialShareEventContext.BLOG,
  };

  return (
    <>
      {!!post && (
        <PostSeo
          post={post}
          sportCategory={sportCategory}
          language={language}
        />
      )}
      <NavigationContainer>
        <Breadcrumb
          sections={[
            { name: 'Blog', to: BLOG },
            {
              name: sportCategory ?? 'all',
              to: generatePath(BLOG_SPORT, {
                sport: sportCategory ?? 'all',
              }),
            },
            {
              name: title,
              to: generatePath(BLOG_SPORT_SLUG, {
                sport: sportCategory ?? 'all',
                slug: post?.slug || '',
              }),
            },
          ]}
        />
        <BackButton
          safePath={`${ORIGIN}${generatePath(BLOG_SPORT, {
            sport: sportCategory ?? 'football',
          })}`}
        />
      </NavigationContainer>
      <Vertical>
        {!!postImage?.url && <img src={postImage.url} alt={title || ''} />}
        <Title as="h3">{title}</Title>
        <MetaAndSharing>
          <Meta excerpt={post} />
          <SocialShare
            {...sharingProps}
            renderButton={({ ShareButton, label, Icon }) => (
              <ShareButton size="small">
                {Icon}
                <span>{label}</span>
              </ShareButton>
            )}
          />
        </MetaAndSharing>
        {/* TEMP FIX to scroll to the hash on first load  - fix because we should not touch the scroll bar  */}
        <ScrollToHash />
        <Content>
          <ClassicBlock data={content!} postImageUrl={postImage?.url} />
          <div ref={bottomRef} />
        </Content>
        <Share>
          <SocialShare
            {...sharingProps}
            renderButton={({ ShareButton, label, Icon }) => (
              <ShareButton size="large" color="tertiary">
                {Icon}
                <span>{label}</span>
              </ShareButton>
            )}
          />
        </Share>
      </Vertical>
    </>
  );
};
